'use client';

import { defineMessages, useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import {
  useLoyaltyApi,
  LoyaltyAccountDataType,
  GetLoyaltyEntitlementConsentEntitlementTypeEnum,
} from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import useSWR from 'swr';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { useNotification } from '@packages/shared/src/providers/NotificationProvider/useNotification';
import { useContext } from 'react';
import { PaybackBoxV2 } from '../PaybackBox/PaybackBoxV2';
import { PaybackAgreementCard } from './PaybackAgreementCard';
import { PaybackCouponCard } from './PaybackCouponCard';
import { PaybackClientProvider } from './PaybackClientProvider';
import { PaybackFetchMethodContext } from './PaybackAccountDataFetchProvider';

const messages = defineMessages({
  ExternalPaybackDataConsentLink: {
    defaultMessage: 'Daten auf PAYBACK.at oder Einwilligungserklärungen auf PAYBACK.at verwalten',
    id: 'Payback.DataConsent.Link',
  },
});

type MyAccountContainerProps = {
  testId?: string;
  revalidateOnFocus?: boolean;
};

export const PaybackConnected: React.FC<MyAccountContainerProps> = ({
  testId = 'myaccount-payback-connected',
  revalidateOnFocus = false,
}) => {
  const loyaltyApi = useLoyaltyApi();
  const { addNotification } = useNotification();
  const { loggedIn } = useLoginState();
  const { formatMessage, locale } = useIntl();
  const {
    order: {
      payback: { links },
    },
  } = useConfig();
  const { fetchMethodRef, setFetchMethod } = useContext(PaybackFetchMethodContext);
  const { data } = useSWR(
    loyaltyApi && loggedIn ? 'getLoyaltyAccountData' : null,
    () =>
      loyaltyApi?.getLoyaltyAccountData({
        ecLocale: locale,
        type: fetchMethodRef.current,
      }),
    {
      revalidateIfStale: false,
      revalidateOnFocus,
      revalidateOnReconnect: false,
      onSuccess: (callbackData) => {
        // After an initial AllUpdated we fetch only Current to avoid unnecessary and expensive payback requests
        setFetchMethod(LoyaltyAccountDataType.Current);
        if (callbackData?.errors) {
          addNotification({
            severity: 'error',
            message: callbackData.errors.elements[0].description,
            duration: 5000,
          });
        }
      },
    },
  );
  return (
    <PaybackClientProvider>
      <div
        data-testid={testId}
        className="bg-color-grey-light flex w-full flex-col gap-1.5 px-1.5 py-2.5 md:gap-2 md:p-3"
      >
        {data && (
          <PaybackBoxV2
            loyaltyProgramData={{
              basePoints: data.totalPointsAmount,
              cardNumber: data.mainCardNumber,
            }}
          />
        )}
        <PaybackCouponCard />
        {data && (
          <>
            <PaybackAgreementCard
              entitlementStatus={data.serviceConsentStatus}
              entitlementType={GetLoyaltyEntitlementConsentEntitlementTypeEnum.Service}
            />
            <PaybackAgreementCard
              entitlementStatus={data.marketingConsentStatus}
              entitlementType={GetLoyaltyEntitlementConsentEntitlementTypeEnum.Marketing}
            />
          </>
        )}
        <a className="text-size-body-sm underline" href={links?.customerData}>
          {formatMessage(messages.ExternalPaybackDataConsentLink)}
        </a>
      </div>
    </PaybackClientProvider>
  );
};
